import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import PermissionGate from "../../../components/common/PermissionGate";
import {
  Users,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  User,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  LayoutList,
  CheckSquare,
  Square,
  FileText as FileIcon,
  Loader2,
} from "lucide-react";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import Button from "../../../components/common/Button/Button";
import candidateService from "../../../api/services/candidateService";
import hrService from "../../../api/services/hrService";
import jobService from "../../../api/services/jobService";
import { API_BASE_URL } from "../../../api/config";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import CustomAlert from "../../../components/common/CustomAlert";
import { formatDate } from "../../../utils/stringUtils";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import CandidateBulkUploadModal from "./CandidateBulkUploadModal";
import { Upload } from "lucide-react";
import {
  buildTimestampedFilename,
} from "../../../utils/export";
import toast from "react-hot-toast";

const CandidateProfileManagement = ({ onViewCandidate }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { alertState, showSuccess, showError, showWarning, hideAlert } = useCustomAlert();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "all");
  const [filterDepartment, setFilterDepartment] = useState(searchParams.get('department') || "all");
  const [filterJobId, setFilterJobId] = useState(searchParams.get('jobId') || "all");
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: Number(searchParams.get('page')) || 1,
    totalPages: 1,
    totalCandidates: 0,
    hasNext: false,
    hasPrev: false,
    limit: Number(searchParams.get('limit')) || 10,
  });
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || "",
    endDate: searchParams.get('endDate') || ""
  });
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(searchParams.get('sortBy') || "appliedDate");
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || "desc");

  // View mode state
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper function to capitalize names
  const capitalizeName = (name) => {
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Helper: truncate long strings for tidy listing
  const truncate = (value, max = 30) => {
    if (!value && value !== 0) return "";
    const str = String(value);
    return str.length > max ? str.slice(0, max - 1) + "…" : str;
  };

  // Load departments and jobs on component mount
  useEffect(() => {
    loadDepartments();
    loadJobs();
  }, []);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'department', 'jobId', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterDepartment && filterDepartment !== 'all') params.set('department', filterDepartment);
      if (filterJobId && filterJobId !== 'all') params.set('jobId', filterJobId);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (pagination.currentPage && pagination.currentPage !== 1) params.set('page', String(pagination.currentPage));
      if (pagination.limit && pagination.limit !== 10) params.set('limit', String(pagination.limit));
      if (sortColumn && sortColumn !== 'appliedDate') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterDepartment, filterJobId, dateRange, pagination.currentPage, pagination.limit, sortColumn, sortDirection, setSearchParams]);

  // Load candidates on component mount
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        loadCandidates(pagination.currentPage, pagination.limit);
      },
      searchTerm ? 500 : 0,
    ); // Debounce search by 500ms

    return () => clearTimeout(timeoutId);
  }, [filterStatus, filterDepartment, filterJobId, searchTerm, dateRange]);

  // Ref to maintain focus on search input
  const searchInputRef = useRef(null);

  // Maintain focus on search input after search
  useEffect(() => {
    if (searchInputRef.current && searchTerm) {
      searchInputRef.current.focus();
    }
  }, [candidates, searchTerm]);

  // Load departments from backend
  const loadDepartments = async () => {
    try {
      setDepartmentsLoading(true);

      const result = await hrService.getDepartments();

      if (result && result.success && result.data) {
        // Transform departments data for dropdown
        const departmentOptions = result.data.map((dept) => ({
          value: dept.name,
          label: dept.name,
        }));

        setDepartments(departmentOptions);
      } else {
        showError(result?.error || "Failed to load departments");
        // Fallback to hardcoded departments if API fails
        setDepartments([
          { value: "Engineering", label: "Engineering" },
          { value: "Sales", label: "Sales" },
          { value: "Marketing", label: "Marketing" },
          { value: "Human Resources", label: "Human Resources" },
          { value: "Operations", label: "Operations" },
          { value: "Finance", label: "Finance" },
          { value: "IT", label: "Information Technology" },
          { value: "Customer Service", label: "Customer Service" },
          { value: "Legal", label: "Legal" },
          { value: "Administration", label: "Administration" },
        ]);
      }
    } catch (error) {
      showError("Failed to load departments");
      // Fallback to hardcoded departments if API fails
      setDepartments([
        { value: "Engineering", label: "Engineering" },
        { value: "Sales", label: "Sales" },
        { value: "Marketing", label: "Marketing" },
        { value: "Human Resources", label: "Human Resources" },
        { value: "Operations", label: "Operations" },
        { value: "Finance", label: "Finance" },
        { value: "IT", label: "Information Technology" },
        { value: "Customer Service", label: "Customer Service" },
        { value: "Legal", label: "Legal" },
        { value: "Administration", label: "Administration" },
      ]);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Load active jobs from backend
  const loadJobs = async () => {
    try {
      setJobsLoading(true);

      // Fetch active jobs (or all, but user requested active)
      const result = await jobService.getJobs({ status: "active", limit: 100 });

      if (result && result.success && result.data) {
        // Extract jobs from response based on normalizeJobsResponse logic
        let jobsArray = [];
        if (Array.isArray(result.data.jobs)) {
          jobsArray = result.data.jobs;
        } else if (Array.isArray(result.data.data)) {
          jobsArray = result.data.data;
        } else if (Array.isArray(result.data)) {
          jobsArray = result.data;
        }

        const jobOptions = jobsArray.map((job) => ({
          value: job.jobId || job.id || job._id,
          label: job.title,
        }));

        setJobs(jobOptions);
      } else {
      }
    } catch (error) {
    } finally {
      setJobsLoading(false);
    }
  };

  const loadCandidates = async (page = 1, limit = pagination.limit) => {
    try {
      setLoading(true);

      const params = {
        page,
        limit,
        ...(filterStatus !== "all" && { status: filterStatus }),
        ...(filterDepartment !== "all" && { department: filterDepartment }),
        ...(filterJobId !== "all" && { jobId: filterJobId }),
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
      };

      const result = await candidateService.getCandidates(params);

      // Check if result has the expected structure
      if (result && result.responseCode === 200 && result.data) {
        // Standard API response format
        setCandidates(result.data.candidates || []);
        setPagination(
          result.data.pagination
            ? {
              ...result.data.pagination,
              limit: result.data.pagination.limit || limit,
            }
            : {
              currentPage: 1,
              totalPages: 1,
              totalCandidates: 0,
              hasNext: false,
              hasPrev: false,
              limit,
            },
        );
      } else if (
        result &&
        result.candidates &&
        Array.isArray(result.candidates)
      ) {
        // Direct data format (no wrapper)
        setCandidates(result.candidates);
        setPagination(
          result.pagination
            ? { ...result.pagination, limit: result.pagination.limit || limit }
            : {
              currentPage: 1,
              totalPages: 1,
              totalCandidates: result.candidates.length,
              hasNext: false,
              hasPrev: false,
              limit,
            },
        );
      } else {
        showError(result?.responseMessage || "Failed to load candidates");
        setCandidates([]);
      }
    } catch (error) {
      showError("Failed to load candidates");
      setCandidates([]);
    } finally {
      setLoading(false);

      // Maintain focus on search input after loading
      if (searchInputRef.current && searchTerm) {
        setTimeout(() => {
          searchInputRef.current.focus();
        }, 100);
      }
    }
  };

  // Permission functions (kept for backward compatibility, but use PermissionGate in JSX)
  const canViewCandidate = () => true;
  const canEditCandidate = () => true;
  const canDeleteCandidate = () => true;
  const canAddCandidate = () => true;

  const handleSearch = (e) => {
    let value = e.target.value;
    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
    value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, "");
    // Enforce max length
    if (value.length > 100) {
      value = value.substring(0, 100);
    }
    setSearchTerm(value);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };

  const handleDepartmentFilter = (department) => {
    setFilterDepartment(department);
  };

  const handleJobFilter = (jobId) => {
    setFilterJobId(jobId);
  };

  const handleViewCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    if (true) {
      if (onViewCandidate) {
        onViewCandidate(candidate);
      } else {
        navigate(`/view_candidate/${candidate.id}`);
      }
    }
  };

  const handleEditCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    navigate(`/edit_candidate/${candidate.id}`);
  };

  const handleDeleteCandidate = (candidate) => {
    // Permission check is now handled by PermissionGate component
    setCandidateToDelete(candidate);
    setShowDeleteModal(true);
  };

  const confirmDeleteCandidate = async () => {
    if (!candidateToDelete) return;

    try {
      const result = await candidateService.deleteCandidate(
        candidateToDelete.id,
      );

      if (result && result.responseCode === 200) {
        showSuccess("Candidate deleted successfully");
        loadCandidates(pagination.currentPage);
      } else {
        showError(result?.responseMessage || "Failed to delete candidate");
      }
    } catch (error) {
      showError("Failed to delete candidate");
    } finally {
      setShowDeleteModal(false);
      setCandidateToDelete(null);
    }
  };

  const cancelDeleteCandidate = () => {
    setShowDeleteModal(false);
    setCandidateToDelete(null);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sorted candidates for the table
  const sortedCandidates = useMemo(() => {
    if (!sortColumn || !candidates || candidates.length === 0)
      return candidates;

    return [...candidates].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === "candidate") {
        aValue = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        bValue = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
      } else if (sortColumn === "job") {
        aValue = (a.jobTitle || "").toLowerCase();
        bValue = (b.jobTitle || "").toLowerCase();
      } else if (sortColumn === "experience") {
        // Simple numeric sort for experience string like "1-2 years" or "5"
        const getExpVal = (str) => {
          if (!str) return 0;
          const match = str.toString().match(/(\d+)/);
          return match ? parseInt(match[1]) : 0;
        };
        aValue = getExpVal(a.experience);
        bValue = getExpVal(b.experience);
      } else if (sortColumn === "appliedDate") {
        aValue = new Date(a.appliedDate || 0).getTime();
        bValue = new Date(b.appliedDate || 0).getTime();
      } else {
        aValue = a[sortColumn] || "";
        bValue = b[sortColumn] || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [candidates, sortColumn, sortDirection]);

  // Row selection handlers — toggle: one click = select all, next click = deselect all
  const handleSelectAll = () => {
    const allIds = candidates.map((c) => c.id || c._id);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedRows.has(id));
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(allIds));
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await candidateService.deleteCandidate(id);
        if (result && result.responseCode === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      showSuccess(
        `${successCount} candidate(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ""}`,
      );
      loadCandidates(pagination.currentPage);
    } else {
      showError("Failed to delete selected candidates");
    }
  };

  // Reset selection when filters change (skip on initial mount to preserve URL state)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setSelectedRows(new Set());
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filterStatus, filterDepartment, filterJobId, searchTerm, dateRange]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  const buildExportFilename = (exportTypeSlug, extension) =>
    buildTimestampedFilename("candidate-profiles", exportTypeSlug, extension);

  const buildActiveCandidateFilters = useCallback(
    (overrides = {}) => ({
      ...(filterStatus !== "all" ? { status: filterStatus } : {}),
      ...(filterDepartment !== "all" ? { department: filterDepartment } : {}),
      ...(filterJobId !== "all" ? { jobId: filterJobId } : {}),
      ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
      ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
      ...(dateRange.endDate ? { endDate: dateRange.endDate } : {}),
      ...overrides,
    }),
    [filterStatus, filterDepartment, filterJobId, searchTerm, dateRange]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
                  },
        body: JSON.stringify({
          module: "candidate-profiles",
          format,
          scope,
          filters: overrideFilters ?? buildActiveCandidateFilters(),
          page: overridePage ?? pagination.currentPage,
          limit: overrideLimit ?? pagination.limit,
          sortBy: sortColumn,
          sortOrder: sortDirection,
          selectedIds,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Export request failed");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = buildExportFilename(scope || "data", format === "xlsx" ? "xlsx" : format);
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      buildActiveCandidateFilters,
      pagination.currentPage,
      pagination.limit,
      sortColumn,
      sortDirection,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleCandidateSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error("No selected candidates found to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} candidate(s) exported successfully!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleCandidateScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !(sortedCandidates || []).length) {
          toast.error("No candidates to export on current page.");
          return;
        }

        if (scope === "dateRange") {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: buildActiveCandidateFilters({ startDate, endDate }),
          });
        } else if (scope === "all") {
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
          });
        } else {
          return;
        }

        setShowExportModal(false);
        toast.success("Export downloaded successfully!");
      } catch (error) {
        toast.error(
          error?.responseMessage || error?.message || "An error occurred during export"
        );
      } finally {
        setExportLoading(false);
      }
    },
    [sortedCandidates, buildActiveCandidateFilters, requestBackendExport]
  );

  const handleMoveCandidate = async (candidate, newStatus) => {
    try {
      const result = await candidateService.moveCandidate(
        candidate.id,
        newStatus,
      );
      if (result && result.responseCode === 200) {
        showSuccess(`Candidate moved to ${newStatus} successfully`);
        loadCandidates(pagination.currentPage);
      } else {
        showError(result?.responseMessage || "Failed to move candidate");
      }
    } catch (error) {
      showError("Failed to move candidate");
    }
  };

  const handleDownloadResume = async (candidate) => {
    try {
      const candidateName =
        candidate.name ||
        `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() ||
        "this candidate";
      const candidateId = candidate.id || candidate._id;

      if (!candidate.resume) {
        showWarning("Document not found");
        return;
      }

      const parseFilenameFromContentDisposition = (contentDisposition) => {
        if (!contentDisposition) return "";

        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (utf8Match?.[1]) {
          try {
            return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, "");
          } catch (_) {
            return utf8Match[1].replace(/(^"|"$)/g, "");
          }
        }

        const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
        return simpleMatch?.[1]
          ? simpleMatch[1].trim().replace(/(^"|"$)/g, "")
          : "";
      };

      const ensureFilenameHasExtension = (filename, mimeType) => {
        const clean = String(
          filename || `${candidateName.replace(/\s+/g, "_")}_Resume`,
        ).trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const type = String(mimeType || "").toLowerCase();
        if (type === "application/pdf") return `${clean}.pdf`;
        if (type === "application/msword") return `${clean}.doc`;
        if (
          type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return `${clean}.docx`;
        if (type === "text/plain") return `${clean}.txt`;
        return `${clean}.pdf`;
      };
      const response = await authFetch(
        `${API_BASE_URL}/recruitment/candidates/${candidateId}/resume`,
        {
          credentials: "include",
          method: "GET",
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          showWarning("Document not found");
          return;
        }
        throw new Error("Failed to download resume");
      }

      const blob = await response.blob();
      const headerFileName = parseFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      );
      const fallbackFileName =
        candidate?.resume?.originalName ||
        candidate?.resume?.filename ||
        `${candidateName.replace(/\s+/g, "_")}_Resume`;
      const resolvedFileName = ensureFilenameHasExtension(
        headerFileName || fallbackFileName,
        blob.type || candidate?.resume?.mimetype || candidate?.resume?.mimeType,
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      showSuccess("Resume download started");
    } catch (error) {
      showError("Failed to download resume. Please try again.");
    }
  };



  const handleAddCandidate = () => {
    // Permission check is handled by PermissionGate component
    navigate("/add_candidate");
  };

  const handlePageChange = (page) => {
    setSelectedRows(new Set());
    loadCandidates(page, pagination.limit);
  };

  const handleLimitChange = (limit) => {
    setSelectedRows(new Set());
    setPagination((prev) => ({ ...prev, limit }));
    loadCandidates(1, limit);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "applied":
        return "bg-blue-100 text-blue-800";
      case "shortlisted":
        return "bg-yellow-100 text-yellow-800";
      case "interviewed":
        return "bg-purple-100 text-purple-800";
      case "interview":
        return "bg-orange-100 text-purple-800";
      case "offered":
        return "bg-green-100 text-green-800";
      case "hired":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "applied":
        return <Clock className="h-4 w-4" />;
      case "shortlisted":
        return <Star className="h-4 w-4" />;
      case "interviewed":
        return <User className="h-4 w-4" />;
      case "offered":
        return <CheckCircle className="h-4 w-4" />;
      case "hired":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "applied", label: "Applied" },
    { value: "shortlisted", label: "Shortlisted" },
    { value: "interviewed", label: "Interviewed" },
    { value: "offered", label: "Offered" },
    { value: "hired", label: "Hired" },
    { value: "rejected", label: "Rejected" },
  ];

  // Dynamic department options with "All Departments" option
  const departmentOptions = [
    { value: "all", label: "All Departments" },
    ...departments,
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Candidate Profile Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and track candidate profiles throughout the recruitment
            process
          </p>
        </div>
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-all duration-200 ${viewMode === "grid"
                ? "bg-white text-agrosaf-green shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
                }`}
              title="Grid View"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md transition-all duration-200 ${viewMode === "list"
                ? "bg-white text-agrosaf-green shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
                }`}
              title="List View"
            >
              <LayoutList className="h-4 w-4" />
            </button>
          </div>
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
              >
                {selectedRows.size > 0
                  ? `Export (${selectedRows.size})`
                  : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCandidateSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="outline"
              icon={<Upload className="h-4 w-4" />}
              onClick={() => setShowBulkUploadModal(true)}
            >
              Bulk Upload
            </Button>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleAddCandidate}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Add Candidate
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search candidate By Name, Email or Job Title"
              value={searchTerm}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (
                  !/^[a-zA-Z0-9\s@._+-]$/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Job Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterJobId}
              onChange={(e) => handleJobFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green max-w-xs"
              disabled={jobsLoading}
            >
              <option value="all">All Jobs</option>
              {jobsLoading ? (
                <option value="all" disabled>
                  Loading jobs...
                </option>
              ) : (
                jobs.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    title={option.label}
                  >
                    {option.label.length > 30
                      ? option.label.substring(0, 27) + "..."
                      : option.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Department Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterDepartment}
              onChange={(e) => handleDepartmentFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green max-w-xs"
              disabled={departmentsLoading}
            >
              {departmentsLoading ? (
                <option value="all">Loading departments...</option>
              ) : (
                departmentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </div>

          {/* Clear Filters */}
          {(searchTerm ||
            filterStatus !== "all" ||
            filterDepartment !== "all" ||
            filterJobId !== "all" ||
            dateRange.startDate !== "" ||
            dateRange.endDate !== "") && (
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                    setFilterDepartment("all");
                    setFilterJobId("all");
                    setDateRange({ startDate: "", endDate: "" });
                  }}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  title="Clear all filters"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Candidates List / Grid */}
      <div className="space-y-4">
        {candidates.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No candidates found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ||
                filterStatus !== "all" ||
                filterDepartment !== "all"
                ? "Try adjusting your search criteria"
                : "Get started by adding your first candidate"}
            </p>
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleAddCandidate}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Add Candidate
              </Button>
            </PermissionGate>
          </div>
        ) : viewMode === "list" ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-2 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4 cursor-pointer"
                        checked={
                          candidates.length > 0 &&
                          candidates.every((c) =>
                            selectedRows.has(c.id || c._id),
                          )
                        }
                        onChange={() => { }}
                        onClick={handleSelectAll}
                        title={
                          candidates.length > 0 &&
                            candidates.every((c) =>
                              selectedRows.has(c.id || c._id),
                            )
                            ? "Deselect all"
                            : "Select all"
                        }
                      />
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("candidate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Candidate</span>
                        {sortColumn === "candidate" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("job")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Job & Department</span>
                        {sortColumn === "job" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === "status" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("experience")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Experience</span>
                        {sortColumn === "experience" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("appliedDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Applied Date</span>
                        {sortColumn === "appliedDate" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedCandidates.map((candidate) => (
                    <tr
                      key={candidate.id || candidate._id}
                      className={`hover:bg-gray-50 transition-colors ${selectedRows.has(candidate.id || candidate._id) ? "bg-agrosaf-green/5" : ""}`}
                    >
                      <td className="px-6 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                          checked={selectedRows.has(
                            candidate.id || candidate._id,
                          )}
                          onChange={() =>
                            handleSelectRow(candidate.id || candidate._id)
                          }
                        />
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="">
                            <div className="text-sm font-medium text-gray-900">
                              {truncate(
                                `${capitalizeName(candidate.firstName)} ${capitalizeName(candidate.lastName)}`.trim(),
                                26,
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {truncate(candidate.email?.toLowerCase(), 28)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {truncate(candidate.phone, 18)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {truncate(candidate.jobTitle, 22)}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {truncate(
                            candidate.department || candidate.jobDepartment,
                            22,
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            candidate.status,
                          )}`}
                        >
                          {getStatusIcon(candidate.status)}
                          <span className="ml-1 capitalize">
                            {candidate.status}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {candidate.experience?.name
                          ? candidate.experience.name
                            .toLowerCase()
                            .includes("year")
                            ? candidate.experience.name
                            : `${candidate.experience.name} Years`
                          : "N/A"}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                        {candidate.appliedDate
                          ? formatDate(candidate.appliedDate, {
                            includeTime: true,
                          })
                          : "N/A"}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <PermissionGate
                            permission="recruitment"
                            action="read"
                          >
                            <button
                              onClick={() => handleViewCandidate(candidate)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate
                            permission="recruitment"
                            action="update"
                          >
                            <button
                              onClick={() => handleEditCandidate(candidate)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Candidate"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          {candidate.resume && (
                            <PermissionGate
                              permission="recruitment"
                              action="read"
                            >
                              <button
                                onClick={() => handleDownloadResume(candidate)}
                                className="text-green-600 hover:text-green-900"
                                title="Download Resume"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          <PermissionGate
                            permission="recruitment"
                            action="delete"
                          >
                            <button
                              onClick={() => handleDeleteCandidate(candidate)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Candidate"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedCandidates.map((candidate) => (
              <div
                key={candidate.id || candidate._id}
                className={`bg-white rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md relative group ${selectedRows.has(candidate.id || candidate._id)
                  ? "border-agrosaf-green ring-1 ring-agrosaf-green"
                  : "border-gray-200"
                  }`}
              >
                {/* Selection Overlay */}
                <div className="absolute top-4 left-4 z-10">
                  <button
                    onClick={() =>
                      handleSelectRow(candidate.id || candidate._id)
                    }
                    className={`transition-colors p-1 rounded-md ${selectedRows.has(candidate.id || candidate._id)
                      ? "text-agrosaf-green"
                      : "text-gray-300 group-hover:text-gray-400"
                      }`}
                  >
                    {selectedRows.has(candidate.id || candidate._id) ? (
                      <CheckSquare className="h-5 w-5 fill-current" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-4 pl-8">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full bg-agrosaf-green flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {capitalizeName(candidate.firstName)?.charAt(0)}
                        {capitalizeName(candidate.lastName)?.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 group-hover:text-agrosaf-green transition-colors">
                          {truncate(
                            `${capitalizeName(candidate.firstName)} ${capitalizeName(candidate.lastName)}`.trim(),
                            20,
                          )}
                        </h4>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <span className="truncate max-w-[150px]">
                            {candidate.jobTitle}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(
                        candidate.status,
                      )}`}
                    >
                      {candidate.status}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                      <span className="truncate">{candidate.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <Star className="h-4 w-4" />
                      </div>
                      <span>
                        {candidate?.experience?.name
                          ? candidate.experience.name
                            .toLowerCase()
                            .includes("year")
                            ? candidate.experience.name
                            : `${candidate.experience.name} Years`
                          : "N/A"}{" "}
                        Experience
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center mr-3 text-gray-400">
                        <Clock className="h-4 w-4" />
                      </div>
                      <span>
                        Applied{" "}
                        {candidate.appliedDate
                          ? formatDate(candidate.appliedDate, {
                            includeTime: false,
                          })
                          : "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex -space-x-1">
                      <PermissionGate permission="recruitment" action="read">
                        <button
                          onClick={() => handleViewCandidate(candidate)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </PermissionGate>
                      <PermissionGate permission="recruitment" action="update">
                        <button
                          onClick={() => handleEditCandidate(candidate)}
                          className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Edit Candidate"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </PermissionGate>
                      {candidate.resume && (
                        <PermissionGate permission="recruitment" action="read">
                          <button
                            onClick={() => handleDownloadResume(candidate)}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title="Download Resume"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      )}
                    </div>
                    <PermissionGate permission="recruitment" action="delete">
                      <button
                        onClick={() => handleDeleteCandidate(candidate)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Candidate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <PaginationControls
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalCandidates}
        limit={pagination.limit}
        hasNextPage={pagination.hasNext}
        hasPrevPage={pagination.hasPrev}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Candidate
                  </h3>
                </div>
              </div>

              <div className="mb-6 space-y-2">
                <p className="text-sm text-gray-600 break-words">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900 break-words">
                    {candidateToDelete?.firstName} {candidateToDelete?.lastName}
                  </span>
                  ?
                </p>
                <p className="text-sm text-gray-500 break-words">
                  This action cannot be undone. All candidate data, including
                  resume and application history, will be permanently deleted.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeleteCandidate}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <PermissionGate permission="recruitment" action="delete">
                  <button
                    onClick={confirmDeleteCandidate}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Candidate
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Selected Candidates
                  </h3>
                </div>
              </div>
              <div className="mb-6 space-y-2">
                <p className="text-sm text-gray-600">
                  You are about to permanently delete{" "}
                  <span className="font-semibold text-red-600">
                    {selectedRows.size} candidate(s)
                  </span>
                  .
                </p>
                <p className="text-sm text-gray-500">
                  This action cannot be undone. All candidate data, including
                  resumes and application history, will be permanently deleted.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedRows.size} Candidate(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Candidates"
        showDateRange
        scopedFormatExport={{
          onExport: handleCandidateScopedExport,
          formats: ["csv", "xlsx", "pdf"],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
      {/* Bulk Upload Modal */}
      <CandidateBulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => loadCandidates(pagination.currentPage)}
        jobs={jobs}
        departments={departments}
      />

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default CandidateProfileManagement;
